import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { notificationsAPI, statsAPI } from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  LayoutDashboard,
  Users,
  User,
  FileText,
  Package,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  Sparkles,
  Mail,
  ClipboardList,
  BarChart3,
  Globe,
  MessageSquare,
  Calendar,
  CheckSquare,
  Wallet,
  Wrench,
  Image,
  FolderCog,
  Bell,
  ExternalLink,
  Truck,
  Briefcase,
  PenLine,
  Target,
  Shield,
} from 'lucide-react';

// === STRUCTURE DES MENUS ===

// 1. Tableau de bord (seul en haut)
const dashboardItem = { path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard };

// 2. Section VENTE
const venteItems = [
  { path: '/admin/opportunities', label: 'Opportunités', icon: Target },
  {
    path: '/admin/devis',
    label: 'Devis',
    icon: FileText,
    subItems: [
      { path: '/admin/devis', label: 'Tous les devis' },
      { path: '/admin/devis/nouveau', label: 'Nouveau devis' },
      { path: '/admin/devis/nouveau?mode=service', label: 'Nouveau devis prestation' },
    ]
  },
  { path: '/admin/financement', label: 'Financement', icon: Wallet },
  { 
    path: '/admin/catalogue', 
    label: 'Catalogue', 
    icon: Package,
    subItems: [
      { path: '/admin/catalogue', label: 'Tout le catalogue' },
      { path: '/admin/catalogue?marque=liugong', label: 'Liugong' },
      { path: '/admin/catalogue?marque=rippa', label: 'Rippa' },
      { path: '/admin/catalogue?marque=lonking', label: 'Lonking' },
      { path: '/admin/catalogue?marque=autre', label: 'Autres' },
      { path: '/admin/accessoires-fournisseur', label: 'Accessoires & Pièces' },
    ]
  },
  { 
    path: '/admin/clients', 
    label: 'Contacts', 
    icon: Users,
    subItems: [
      { path: '/admin/clients', label: 'Tous les contacts' },
      { path: '/admin/clients?type=client', label: 'Clients' },
      { path: '/admin/fournisseurs', label: 'Fournisseurs' },
      { path: '/admin/cabinets-snc', label: 'Cabinets SNC' },
      { path: '/admin/clients?type=banque', label: 'Banques' },
    ]
  },
  { path: '/admin/taches', label: 'Tâches', icon: CheckSquare },
  { path: '/admin/calendrier', label: 'Calendrier', icon: Calendar },
  { path: '/admin/messagerie', label: 'Messagerie', icon: MessageSquare },
  { path: '/admin/sav', label: 'SAV & Garantie', icon: Wrench },
  { path: '/admin/mediatheque', label: 'Médiathèque', icon: Image },
  { path: '/admin/ai-lead', label: 'Création IA', icon: Sparkles },
];

// 3. Section ADMINISTRATION (visible uniquement pour les admins)
const adminItems = [
  { path: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users },
  { path: '/admin/roles', label: 'Rôles & Permissions', icon: Shield },
  { path: '/admin/rapports/activites', label: 'Rapport activités', icon: BarChart3 },
  { path: '/admin/blog', label: 'Blog SEO', icon: PenLine },
  { path: '/admin/site-web', label: 'Site Web', icon: Globe },
  { path: '/admin/emailing', label: 'Emailing', icon: Mail },
  { path: '/admin/email-templates', label: 'Modèles d\'emails', icon: FileText },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/parametres', label: 'Paramètres', icon: Settings },
];

// 4. Section PLUS (supprimée - contenu déplacé dans Administration)
const plusItems = [];

// Maintien de la compatibilité avec l'ancien code
const mainNavItems = [dashboardItem, ...venteItems];
const moreNavItems = plusItems;

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [venteOpen, setVenteOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);
  const [plusOpen, setPlusOpen] = useState(false);
  const [devisOpen, setDevisOpen] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [devisToAssign, setDevisToAssign] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  
  // Fetch notifications and stats
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          notificationsAPI.getAll({ limit: 10, unread_only: false }),
          notificationsAPI.getUnreadCount()
        ]);
        setNotifications(notifRes.data);
        setUnreadCount(countRes.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    const fetchDevisToAssign = async () => {
      if (isAdmin) {
        try {
          const res = await statsAPI.getDevisToAssignCount();
          setDevisToAssign(res.data.count);
        } catch (error) {
          console.error('Error fetching devis to assign:', error);
        }
      }
    };
    
    fetchNotifications();
    fetchDevisToAssign();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchDevisToAssign();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isAdmin]);
  
  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await notificationsAPI.markAsRead(notif.id);
        setNotifications(prev => prev.map(n => n.id === notif.id ? {...n, read: true} : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    // Navigate to link
    if (notif.link) {
      navigate(notif.link);
      setNotifOpen(false);
    } else {
      // Fallback intelligent selon le contenu du message
      const msg = (notif.message || '').toLowerCase();
      if (msg.includes('devis') || msg.includes('relancer') || msg.includes('relance')) {
        navigate('/admin/devis?relance=true');
        setNotifOpen(false);
      } else if (msg.includes('opportunit')) {
        navigate('/admin/opportunities');
        setNotifOpen(false);
      }
    }
  };
  
  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };
  
  // Company branding
  const COMPANY_NAME = "IDMAT OI";
  const COMPANY_LOGO = "https://idmatoi.com/mailing/logo_idmatoi.png";

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      end={item.path === '/admin'}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm',
          isActive && 'bg-slate-100 text-slate-900 font-medium'
        )
      }
      data-testid={`nav-${item.path.replace('/admin/', '').replace('/admin', 'dashboard') || 'dashboard'}`}
    >
      <item.icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate flex-1">{item.label}</span>
      {/* Badge pour devis à affecter */}
      {item.path === '/admin/devis' && isAdmin && devisToAssign > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center animate-pulse">
          {devisToAssign}
        </span>
      )}
    </NavLink>
  );

  const SubNavItem = ({ item }) => (
    <NavLink
      to={item.path}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 ml-4 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm',
          isActive && 'bg-slate-100 text-slate-900 font-medium'
        )
      }
    >
      <item.icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="h-8 object-contain" />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-60 bg-white border-r border-slate-200 z-40 transition-transform duration-300 lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-200 flex-shrink-0">
          <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="h-8 object-contain" />
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {/* Tableau de bord */}
          <NavItem item={dashboardItem} />
          
          {/* Section VENTE - Collapsible */}
          <Collapsible open={venteOpen} onOpenChange={setVenteOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-colors rounded-md text-sm font-medium mt-2">
              <div className="flex items-center gap-3">
                <Briefcase className="w-4 h-4" />
                <span>Vente</span>
              </div>
              {venteOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              {venteItems.map((item) => (
                item.subItems ? (
                  // Item avec sous-menu (Contacts ou Catalogue)
                  <Collapsible 
                    key={item.path} 
                    open={
                      item.label === 'Contacts'
                        ? contactsOpen
                        : item.label === 'Catalogue'
                        ? catalogueOpen
                        : devisOpen
                    }
                    onOpenChange={
                      item.label === 'Contacts'
                        ? setContactsOpen
                        : item.label === 'Catalogue'
                        ? setCatalogueOpen
                        : setDevisOpen
                    }
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 ml-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm">
                      <div className="flex items-center gap-3">
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </div>
                      {(item.label === 'Contacts'
                        ? contactsOpen
                        : item.label === 'Catalogue'
                        ? catalogueOpen
                        : devisOpen) ? 
                        <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-1 mt-1 ml-4">
                      {item.subItems.map((subItem) => (
                        <NavLink
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 px-3 py-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors rounded-md text-xs',
                              isActive && 'bg-slate-100 text-slate-700 font-medium'
                            )
                          }
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                          <span>{subItem.label}</span>
                        </NavLink>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ) : (
                  // Item simple
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 ml-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm',
                        isActive && 'bg-slate-100 text-slate-900 font-medium'
                      )
                    }
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate flex-1">{item.label}</span>
                    {item.path === '/admin/devis' && isAdmin() && devisToAssign > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center animate-pulse">
                        {devisToAssign}
                      </span>
                    )}
                  </NavLink>
                )
              ))}
            </CollapsibleContent>
          </Collapsible>
          
          {/* Section ADMINISTRATION - Admin only */}
          {isAdmin() && (
            <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors rounded-md text-sm font-medium mt-2">
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4" />
                  <span>Administration</span>
                </div>
                {adminOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-1 mt-1">
                {adminItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 ml-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm',
                        isActive && 'bg-slate-100 text-slate-900 font-medium'
                      )
                    }
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {/* Section PLUS */}
          <Collapsible open={plusOpen} onOpenChange={setPlusOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-slate-700 hover:bg-slate-100 transition-colors rounded-md text-sm font-medium mt-2">
              <div className="flex items-center gap-3">
                <FolderCog className="w-4 h-4" />
                <span>Plus</span>
              </div>
              {plusOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 mt-1">
              {plusItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 ml-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors rounded-md text-sm',
                      isActive && 'bg-slate-100 text-slate-900 font-medium'
                    )
                  }
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </nav>

        {/* Notifications - Above User Menu */}
        <div className="px-3 py-2 border-t border-slate-200 flex-shrink-0">
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between h-auto py-2"
                data-testid="notifications-trigger"
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  <span className="text-sm">Notifications</span>
                </div>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="font-medium text-sm">Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-auto py-1" onClick={handleMarkAllRead}>
                    Tout marquer lu
                  </Button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  Aucune notification
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem
                    key={notif.id}
                    className={cn(
                      "flex flex-col items-start gap-1 p-3 cursor-pointer",
                      !notif.read && "bg-blue-50"
                    )}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <span className={cn(
                        "text-sm font-medium",
                        !notif.read && "text-blue-700"
                      )}>
                        {notif.title}
                      </span>
                      {!notif.read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </div>
                    <span className="text-xs text-slate-600 line-clamp-2">{notif.message}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(notif.created_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'short', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* User Menu - Fixed at bottom */}
        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between h-auto py-2"
                data-testid="user-menu-trigger"
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-medium text-slate-900 truncate max-w-[120px]">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem disabled>
                <span className="text-slate-500 text-xs truncate">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/admin/mon-profil" className="flex items-center cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Mon Profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-60 pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
